import { Controller } from "@hotwired/stimulus";
import { buildPhotoDropPlaceholder, PHOTO_DROP_IMG_CLASSES } from "../lib/photo_drop";
import { destroy } from "@rails/request.js";
import { normalizeIfHeic } from "../lib/image_normalizer";
import { showConfirmModal } from "../lib/custom_confirm";

const GENERIC_UPLOAD_ERROR = "Unable to upload photo. Please try again.";

// A refused upload (422) carries PhotoValidation's own sentence as its plain-text
// body (UsersController#respond_to_photo_change) — "Photo must be JPG or PNG",
// "Photo is too large (max 2MB)", … — and that is what the agent must read.
class UploadRefused extends Error {}

// Instant avatar upload for the Profile page. Inside a photo-crop scope the
// upload fires on photo-crop:applied — the crop modal's Apply is the explicit
// approval — never on the raw selection. A professional image accepted from
// the headshot-generation modal (#13320) arrives the same way: that controller
// writes the generated file into the input and stamps the generation id on the
// input's dataset; the upload carries the id so the server can mark the
// generation accepted, then announces the saved photo (profile-photo:uploaded)
// so the modal works from the new saved portrait afterwards.
export default class extends Controller {
  static targets = ["status", "error", "removeBtn"];
  static GENERATION_DATASET_KEY = "headshotGenerationId";
  static GENERATION_PARAM = "headshot_generation_id";
  static UPLOADED_EVENT = "profile-photo:uploaded";
  static values = {
    uploadUrl: String,
    removeUrl: String,
    maxSizeBytes: Number,
    sizeErrorMessage: String,
  };

  connect() {
    this.fileInput = this.element.querySelector(
      'input[type="file"][data-profile-photo-input]',
    );

    console.log("[ProfilePhoto] controller connected", {
      hasStatusTarget: this.hasStatusTarget,
      hasFileInput: Boolean(this.fileInput),
    });

    if (this.fileInput) {
      this.boundUploadHandler = this.upload.bind(this);
      // Inside a photo-crop scope the raw change event fires TWICE — once for
      // the user's original selection (which must NOT upload; the crop modal
      // is about to open) and once when the cropped file is written back.
      // photo-crop:applied fires only for the finished crop, so that is the
      // upload trigger there; bare avatars keep the plain change listener.
      this.uploadEventName = this.fileInput.closest('[data-controller~="photo-crop"]')
        ? "photo-crop:applied"
        : "change";
      this.fileInput.addEventListener(this.uploadEventName, this.boundUploadHandler);
    } else {
      console.warn("[ProfilePhoto] file input not found within controller scope");
    }
  }

  disconnect() {
    if (this.fileInput && this.boundUploadHandler) {
      this.fileInput.removeEventListener(this.uploadEventName, this.boundUploadHandler);
    }
  }

  async upload(event) {
    const fileInput = event.currentTarget || this.fileInput;

    if (!(fileInput instanceof HTMLInputElement)) {
      return;
    }

    const selectedFile = fileInput.files?.[0];
    if (!selectedFile) return;

    if (!this.validateFile(selectedFile)) {
      fileInput.value = "";
      return;
    }

    if (!this.hasUploadUrlValue) {
      console.warn("[ProfilePhoto] missing upload URL value");
      return;
    }

    this.setUploading(true, fileInput);

    // Convert iPhone HEIC/HEIF to JPEG in-browser: only Safari can display a
    // HEIC, and the server refuses a raw HEIC upload (#6130, #14350).
    const file = await normalizeIfHeic(selectedFile);

    console.log("[ProfilePhoto] uploading file", {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    const formData = new FormData();
    formData.append("user[photo]", file);
    const generationId = fileInput.dataset[this.constructor.GENERATION_DATASET_KEY];
    if (generationId) formData.append(this.constructor.GENERATION_PARAM, generationId);

    fetch(this.uploadUrlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": this.csrfToken(),
        Accept: "text/vnd.turbo-stream.html",
        "X-Requested-With": "Turbo",
      },
      body: formData,
      credentials: "same-origin",
    })
      .then(async (response) => {
        const body = await response.text();
        if (!response.ok) {
          throw new UploadRefused(this.refusalMessage(response, body));
        }
        if (window.Turbo) {
          window.Turbo.renderStreamMessage(body);
        }
        // The server's turbo-stream targets frames that only exist on the
        // legacy edit page, so update the local avatar directly. The sidebar
        // is NOT updated: its slot wears the producer tier badge, never the
        // profile photo.
        // data: URL, not URL.createObjectURL — blob: URLs die with the
        // document but Turbo snapshots keep the src, so cache restores fetch
        // dead blobs (net::ERR_FILE_NOT_FOUND).
        const previewUrl = await this.previewDataUrl(file).catch(() => null);
        if (previewUrl) {
          this.updateLocalAvatar(previewUrl);
        }
        // A photo now exists, so the "Remove Photo" control becomes relevant.
        this.toggleRemoveButton(true);
        this.showToast("Photo Saved", "bg-success");
        // The announced preview never goes stale on a FileReader hiccup: fall
        // back to an object URL of the very file that was just saved.
        fileInput.dispatchEvent(new CustomEvent(this.constructor.UPLOADED_EVENT, {
          bubbles: true, detail: { previewUrl: previewUrl || URL.createObjectURL(file) },
        }));
      })
      .catch((error) => {
        console.error("Profile photo upload failed", error);
        this.showError(error instanceof UploadRefused ? error.message : GENERIC_UPLOAD_ERROR);
      })
      .finally(() => {
        console.log("[ProfilePhoto] upload finished");
        this.setUploading(false, fileInput);
        fileInput.value = "";
        // Consumed (or refused) with this upload; never rides a later pick.
        delete fileInput.dataset[this.constructor.GENERATION_DATASET_KEY];
      });
  }

  // Only a 422 speaks the validation sentence; anything else (a 500's HTML
  // page, an empty body) falls back to the generic line.
  refusalMessage(response, body) {
    const text = (body || "").trim();
    if (response.status === 422 && text && !text.startsWith("<")) return text;
    return GENERIC_UPLOAD_ERROR;
  }

  validateFile(file) {
    this.clearError();

    if (!file) return true;
    if (!this.hasMaxSizeBytesValue || Number.isNaN(this.maxSizeBytesValue)) {
      return true;
    }

    if (file.size > this.maxSizeBytesValue) {
      this.showError(
        this.hasSizeErrorMessageValue
          ? this.sizeErrorMessageValue
          : "Photo is too large.",
      );
      return false;
    }

    return true;
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("d-none");
    }

    if (typeof window.showToast === "function") {
      window.showToast(message, "bg-danger");
    }
  }

  clearError() {
    if (!this.hasErrorTarget) return;

    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("d-none");
  }

  setUploading(isUploading, fileInput) {
    fileInput.disabled = isUploading;

    if (!this.hasStatusTarget) return;

    if (isUploading) {
      this.statusTarget.classList.remove("d-none");
      this.statusTarget.textContent = "Uploading photo…";
    } else {
      this.statusTarget.classList.add("d-none");
      this.statusTarget.textContent = "";
    }
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta?.getAttribute("content") || "";
  }

  showToast(message, type = "") {
    if (typeof window.showToast === "function") {
      window.showToast(message, type);
    }
  }

  previewDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  updateLocalAvatar(url) {
    const existingImg = this.element.querySelector('img.gl-profile-avatar-img');
    if (existingImg) {
      existingImg.src = url;
      return;
    }
    const placeholder = this.element.querySelector('.gl-profile-avatar-placeholder');
    if (!placeholder) return;
    const newImg = document.createElement('img');
    newImg.src = url;
    newImg.alt = 'Profile photo';
    newImg.classList.add(...PHOTO_DROP_IMG_CLASSES);
    placeholder.replaceWith(newImg);
  }

  // Deletes the profile photo (DELETE /users/:id/photo). The server acknowledges
  // client-side; like upload(), this controller reverts the avatar itself rather
  // than depending on a server-rendered turbo frame.
  async remove(event) {
    event?.preventDefault();

    if (!this.hasRemoveUrlValue) {
      console.warn("[ProfilePhoto] missing remove URL value");
      return;
    }

    const confirmed = await showConfirmModal("Remove your profile photo?", {
      title: "Remove Photo",
      confirmText: "Remove Photo",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      const response = await destroy(this.removeUrlValue, {
        responseKind: "turbo-stream",
      });
      if (!response.ok) {
        throw new Error(`Remove failed with status ${response.statusCode}`);
      }
      this.removeLocalAvatar();
      this.toggleRemoveButton(false);
      this.showToast("Photo Removed", "bg-success");
    } catch (error) {
      console.error("Profile photo removal failed", error);
      this.showError("Unable to remove photo. Please try again.");
    }
  }

  // Reverse of updateLocalAvatar's first-time branch: swap the <img> back to the
  // Drop Photo placeholder so the avatar reflects the now-photoless state.
  removeLocalAvatar() {
    const existingImg = this.element.querySelector('img.gl-profile-avatar-img');
    if (!existingImg) return;
    existingImg.replaceWith(buildPhotoDropPlaceholder());
  }

  // The "Remove Photo" control is only meaningful when a photo exists; keep it in
  // sync with the avatar so uploads reveal it and removals hide it.
  toggleRemoveButton(show) {
    if (!this.hasRemoveBtnTarget) return;
    this.removeBtnTarget.classList.toggle('d-none', !show);
  }
}
